import { bigDealAmount } from "../Dashboard/constants";
import { byId } from "../Dashboard/animations/registry";

// Rekenkant van het animatielab: elke animatie verandert ook echt de cijfers.

const pick = (list) => list[Math.floor(Math.random() * list.length)];

const round50 = (value) => Math.round(value / 50) * 50;

// Willekeurig dealbedrag in hele vijftigtallen.
const roundDeal = (min, max) => round50(min + Math.random() * (max - min));

// Verkoop door deze verkoper van een willekeurige dienst.
const saleBy = (data, name, amount) => ({
  name,
  service: pick(data.soldServices).name,
  amount,
});

const bySize = (a, b) => (b.totalPrice || 0) - (a.totalPrice || 0);

// Telt een bedrag bij een naam op en sorteert opnieuw, zonder de lijst te muteren.
function addTo(list, name, amount) {
  const found = list.some((row) => row.name === name);
  const next = found
    ? list.map((row) =>
        row.name === name
          ? { ...row, totalPrice: (row.totalPrice || 0) + amount }
          : row,
      )
    : [...list, { name, totalPrice: amount }];

  return next.sort(bySize);
}

// Verwerkt één verkoop in alle cijfers die het dashboard laat zien.
export function applySale(data, { name, service, amount }) {
  const rankList = addTo(data.rankList, name, amount);
  const soldServices = service
    ? addTo(data.soldServices, service, amount)
    : data.soldServices;

  const total = data.amount + amount;

  // Bovenste rij van de jaarlijst is de lopende maand en volgt de koploper.
  const leader = rankList[0];
  const yearRankList = data.yearRankList.length
    ? [
        {
          ...data.yearRankList[0],
          name: leader.name,
          totalPrice: leader.totalPrice,
        },
        ...data.yearRankList.slice(1),
      ]
    : data.yearRankList;

  return {
    ...data,
    rankList,
    soldServices,
    yearRankList,
    amount: total,
    scoredTowardsTarget: total,
  };
}

// Cijfertoetsen 1..9: drie verkopers maal klein, medium en groot bedrag.
export const labSellers = ["Ernst", "Bobbie", "En de rest"];
export const labAmounts = [100, 500, 1100];

export const payments = labSellers.flatMap((name, row) =>
  labAmounts.map((amount, column) => ({
    key: String(row * 3 + column + 1),
    name,
    amount,
  })),
);

// Betaling van een cijfertoets, met een willekeurige dienst.
export function paymentSale(data, payment) {
  return saleBy(data, payment.name, payment.amount);
}

// Eigen lat van het lab (niet de echte uit localStorage), afgestemd op de scenario's.
export const labRecord = { name: "Bobbie", amount: 5200 };
export const labBests = {
  Ernst: { amount: 2400 },
  Bobbie: { amount: 5200 },
  "En de rest": { amount: 4000 },
  Ernie: { amount: 2600 },
  Hans: { amount: 1600 },
  Grietje: { amount: 1200 },
  "Johan Derksen": { amount: 1500 },
};

// Hoogt de lat op zoals useSaleEvents live doet: de groei per verkoper telt als één verkoop.
export function raiseRecords(before, next, { record, bests }) {
  const was = new Map(before.rankList.map((row) => [row.name, row.totalPrice || 0]));
  let nextRecord = record;
  const nextBests = { ...bests };

  next.rankList.forEach(({ name, totalPrice }) => {
    const amount = (totalPrice || 0) - (was.get(name) ?? 0);
    if (amount <= 0) return;
    if (amount > (nextRecord?.amount ?? 0)) nextRecord = { name, amount };
    if (amount > (nextBests[name]?.amount ?? 0)) nextBests[name] = { amount };
  });

  return { record: nextRecord, bests: nextBests };
}

// Schaalt iedereen evenredig op tot het team 'short' euro onder het target staat.
// Niet te krap: het percentage wordt afgerond, dus 99,5% telt al als gehaald.
export function nearTarget(data, short = 800) {
  const target = data.teamTarget.amount || 0;
  if (!target) return data;
  return ScaleTeam(data, target - short);
}

// Zet het team op dit percentage van het target, ook boven de 100%.
export function overTarget(data, percent) {
  const target = data.teamTarget.amount || 0;
  if (!target) return data;
  return ScaleTeam(data, round50((target * percent) / 100) + 50);
}

// Schaalt iedereen evenredig op tot het team samen 'total' heeft.
function ScaleTeam(data, total) {
  if (total <= data.amount) return data;

  const factor = total / data.amount;
  const scale = (row) => ({
    ...row,
    totalPrice: round50((row.totalPrice || 0) * factor),
  });
  const rankList = data.rankList.map(scale);
  rankList[0].totalPrice += total - rankList.reduce((sum, row) => sum + row.totalPrice, 0);

  const yearRankList = data.yearRankList.length
    ? [{ ...data.yearRankList[0], ...rankList[0] }, ...data.yearRankList.slice(1)]
    : data.yearRankList;

  return {
    ...data,
    rankList,
    soldServices: data.soldServices.map(scale),
    yearRankList,
    amount: total,
    scoredTowardsTarget: total,
  };
}

// Echte situaties vanaf de begincijfers; 'steps' is wat het dashboard dan hoort te tonen.
//   sales      - verkopen binnen één ophaalronde
//   nearTarget - eerst stil naar vlak onder het target
//   animation  - een losse animatie in plaats van een verkoop
//   party      - het feestje, als met Shift+8
export const labScenarios = [
  {
    id: "sale",
    name: "Normale verkoop",
    steps: "verkoop",
    sales: [{ name: "Hans", amount: 450, service: "Werkplekbeheer" }],
  },
  {
    id: "big",
    name: "Grote deal",
    steps: "verkoop + extra confetti",
    sales: [{ name: "Bobbie", amount: 1650, service: "Firewall as a Service" }],
  },
  {
    id: "two",
    name: "Twee verkopen tegelijk",
    steps: "verkoop + verkoop",
    sales: [
      { name: "En de rest", amount: 1100, service: "VoIP telefonie" },
      { name: "Hans", amount: 450, service: "Werkplekbeheer" },
    ],
  },
  {
    id: "climb",
    name: "Stijger",
    steps: "verkoop + stijger",
    sales: [{ name: "Johan Derksen", amount: 1200, service: "Security awareness" }],
  },
  {
    id: "podium",
    name: "Podiumwissel",
    steps: "verkoop + stijger + podium",
    sales: [{ name: "En de rest", amount: 3600, service: "Werkplekbeheer" }],
  },
  {
    id: "best",
    name: "Eigen record",
    steps: "verkoop + eigen record",
    sales: [{ name: "Ernst", amount: 2950, service: "Managed Cloud Backup" }],
  },
  {
    id: "record",
    name: "Record",
    steps: "verkoop + record",
    sales: [{ name: "Ernst", amount: 5600, service: "Microsoft 365 Business" }],
  },
  {
    id: "record-climb",
    name: "Record en stijger",
    steps: "verkoop + record + podium",
    sales: [{ name: "Ernie", amount: 6400, service: "Managed Cloud Backup" }],
  },
  {
    id: "target",
    name: "Verkoop haalt target",
    steps: "verkoop + target",
    key: "0",
    nearTarget: true,
    sales: [{ name: "Hans", amount: 950, service: "Hosting & domeinen" }],
  },
  {
    id: "record-target",
    name: "Record haalt target",
    steps: "verkoop + record + target",
    nearTarget: true,
    sales: [{ name: "Ernst", amount: 5600, service: "Microsoft 365 Business" }],
  },
  { id: "over-110", name: "Boven target 110%", steps: "boven target", overTarget: 110 },
  { id: "over-125", name: "Boven target 125%", steps: "boven target", overTarget: 125 },
  { id: "over-150", name: "Boven target 150%", steps: "boven target", overTarget: 150 },
  { id: "over-200", name: "Boven target 200%", steps: "dubbel target", overTarget: 200 },
  {
    id: "missed",
    name: "Tv gaat aan",
    steps: "gemiste verkopen",
    animation: "missed-sales",
  },
  {
    id: "party",
    name: "Feest aangezet",
    steps: "feest",
    key: "P",
    party: true,
  },
];

// Een willekeurige verkoop.
export function randomSale(data, { min = 250, max = 2200 } = {}) {
  return saleBy(data, pick(data.rankList).name, roundDeal(min, max));
}

// Verkoop waarmee deze verkoper de plek erboven pakt, of null als die er niet is.
function jumpOver(data, index) {
  const climber = data.rankList[index];
  const above = data.rankList[index - 1];
  if (!climber || !above) return null;

  const gap = (above.totalPrice || 0) - (climber.totalPrice || 0);
  return {
    ...saleBy(data, climber.name, Math.max(50, round50(gap + roundDeal(300, 900)))),
    from: index + 1,
    position: index,
  };
}

// Per animatie de nieuwe cijfers en payload; 'sale' is alleen voor de weergave in het lab.
export function scenarioFor(id, data) {
  const nothing = { data, payload: {}, sale: null };

  switch (id) {
    case "sale-charge":
    case "sale-splash":
    case "row-flash":
    case "gauge-pulse":
    case "screen-glow": {
      const sale = randomSale(data, { min: 250, max: 1800 });
      return { data: applySale(data, sale), payload: sale, sale };
    }

    // Meteen een grote deal, want die buien wil je zien.
    case "confetti-burst": {
      const sale = randomSale(data, { min: bigDealAmount, max: 4500 });
      return { data: applySale(data, sale), payload: {}, sale };
    }

    // Record uit de middenmoot; het vorige record ligt er net onder.
    case "fireworks": {
      const middle = Math.min(3, Math.max(1, data.rankList.length - 2));
      const seller = data.rankList[middle] || data.rankList[0];
      const sale = saleBy(data, seller.name, roundDeal(3000, 6000));
      const previous = round50(sale.amount * 0.85);
      return {
        data: applySale(data, sale),
        payload: { ...sale, previous },
        sale,
      };
    }

    // Eigen record, kleiner dan het bordrecord; de oude beste ligt ruim onder.
    case "personal-best": {
      const sale = saleBy(data, pick(data.rankList).name, roundDeal(1200, 2600));
      const previous = round50(sale.amount * 0.7);
      return {
        data: applySale(data, sale),
        payload: { ...sale, previous },
        sale,
      };
    }

    // Iemand van onderaf schuift een plek op.
    case "rank-up": {
      const index = Math.min(
        data.rankList.length - 1,
        3 + Math.floor(Math.random() * 2),
      );
      const jump = jumpOver(data, index);
      if (!jump) return nothing;
      return {
        data: applySale(data, jump),
        payload: { name: jump.name, position: jump.position, from: jump.from },
        sale: jump,
      };
    }

    // De top 3 wisselt van volgorde.
    case "podium-bounce": {
      const jump = jumpOver(data, 1 + Math.floor(Math.random() * 2));
      if (!jump) return nothing;
      return { data: applySale(data, jump), payload: {}, sale: jump };
    }

    // Het laatste zetje naar de 100%.
    case "target-hit": {
      const missing = Math.max(500, (data.teamTarget.amount || 0) - data.amount);
      const sale = saleBy(data, pick(data.rankList).name, missing);
      const next = applySale(data, sale);
      return { data: next, payload: { amount: next.amount }, sale };
    }

    // Tv-uit is niet na te spelen, dus de vaste democijfers uit de registry.
    case "missed-sales":
    case "missed-nothing":
      return { data, payload: byId(id).demo, sale: null };

    default:
      return nothing;
  }
}
